import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useSpring, animated } from "@react-spring/web";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import LinesEllipsis from "react-lines-ellipsis";

import InfiniteScroll from "../infiniteScroll";
import Thumbnail from "../thumbnail";
import { FileService } from "src/utils/file.service";
import useMeasure from "react-use-measure";
import { fetchGroupChildren } from "src/redux/actions/groups";

import { Group } from "src/types/groups";
import Text from "../text";

import _ from "lodash";
import cx from "classnames";
import styles from "./entityPanel.module.scss";

const defaultAsset = "/images/assets/default_asset.jpg";

interface EntityPanelProps {
  className?: string;
  entity?: Group;
  items?: any[];
  selected: string;
  loading: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityPanel: React.FC<EntityPanelProps> = ({
  className,
  items,
  selected,
  loading,
  onSelection
}) => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={cx(styles.container, className)} ref={scrollContainerRef}>
      {loading? (
        <div className={styles.loadingPlaceholder}>
          {_.times(3, (index) => (
            <PlaceholderCard key={index} />
          ))}
        </div>
      ) : (
        <>
          {items && items.map((item) => (
            <EntityRow
              key={item.id}
              entity={item}
              selected={selected}
              loading={loading}
              onSelection={onSelection}
              scrollContainerRef={scrollContainerRef}
            />
          ))}
        </>
      )}
    </div>
  );
};

interface EntityRowProps {
  entity: any;
  selected: string;
  loading: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const EntityRow: React.FC<EntityRowProps> = ({
  entity,
  selected,
  loading,
  onSelection,
  scrollContainerRef
}) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case "group":
        return GroupRow;
      case "asset":
        return AssetRow;
      default:
        return false;
    }
  }, [entity]);

  if (!Component) {
    return null;
  }

  return React.createElement(Component, { entity, selected, loading, onSelection, scrollContainerRef });
};

const GroupBrowser: React.FC<EntityPanelProps> = ({
  className,
  entity,
  selected,
  loading,
  onSelection
}) => {
  const dispatch = useDispatch();
  const { loadingChildren, items, pagination, fetchedChildren } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
      };
    }

    return {
      loadingChildren: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
      pagination: state.group[entity!.id!]?.pagination,
      fetchedChildren: state.group[entity!.id!]?.fetchedChildren,
    };
  }, _.isEqual);

  useEffect(() => {
    if(!entity) return;
    if (fetchedChildren) return;

    dispatch(fetchGroupChildren(
      {
        id: entity.id,
        page: 0,
        limit: 25,
        sort: {
          type: "name",
          order: "asc",
        },
      }
    ));
  }, [entity]);

  const filteredAssetsFromChildren = (children: any[]) => {
    return children.filter((item: any) => _.get(item, "entityType") === "asset");
  };

  const filteredItems = filteredAssetsFromChildren(items || []);

  const loadMoreAssets = () => {
    if (!entity?.id) return;

    dispatch(fetchGroupChildren(
      {
        id: entity.id,
        page: (pagination?.currentPage || 0) + 1,
        limit: 25,
        sort: {
          type: "name",
          order: "asc",
        },
      }
    ))
  }

  return (
    <div className={cx(styles.stacks)}>
      <InfiniteScroll
        className={cx(styles.scroll, styles.groupBrowser)}
        loading={loadingChildren}
        hasMore={pagination?.hasMoreItems}
        loadMore={loadMoreAssets}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <PlaceholderAssetCard key={index} />
              ))}
            </>
          );
        }}
      >
        <EntityPanel
          className={className}
          items={filteredItems}
          selected={selected}
          loading={loading}
          onSelection={onSelection}
        />
      </InfiniteScroll>
    </div>
  );
};

const GroupRow: React.FC<EntityRowProps> = ({ entity, selected, loading, onSelection, scrollContainerRef }) => {
  const [expanded, setExpanded] = React.useState(true);
  const [mountChildren, setMountChildren] = React.useState(true);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [clamped, setClamped] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring(
    {
      opacity: expanded ? 1 : 0,
      from: { opacity: 0, height: 0 },
    },
    [expanded, containerBounds],
  );

  const handleClick = () => {
    setExpanded(true);
    setMountChildren(true);
  };

    const handleReflow = (rleState: any) => {
    const isClamped = rleState.clamped;
    setClamped(isClamped);
  };

  const nameLength = _.size(entity?.name.replace(/\s/g, ''));
  let width = 0;

  // no white space in name
  if(_.isEqual(_.size(entity.name), nameLength)){
    if(nameLength > 2 && nameLength <= 6) width = nameLength + 1;
    else width = nameLength;
  } else {
    width = nameLength;
  }

  return (
    <>
      <div
        className={cx(styles.entity, styles.expandable, {
          [styles.expanded]: expanded,
        })}
        onClick={handleClick}
      >
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{entity?.name}</Tooltip>}
          show={showTooltip && clamped}
        >
          <div
            className={styles.groupNameWrapper}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <LinesEllipsis 
              text={entity?.name}
              maxLine='1'
              ellipsis='...'
              trimRight
              basedOn='letters'
              onReflow={handleReflow}
              className={styles.groupName}
            />
            <div
              className={styles.groupNameOverlay}
              style={{ width: clamped ? "85%" : `${width || 0}ch` }}
            ></div>
          </div>
        </OverlayTrigger>
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser entity={entity} selected={selected} loading={loading} onSelection={onSelection} />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
};

const AssetRow: React.FC<EntityRowProps> = ({ entity, selected, loading, onSelection, scrollContainerRef }) => {

  const cardRef = useRef<HTMLDivElement>(null);
  const fileType = FileService.getFileType(entity.metadata);
  const active = entity.id === selected;

    useEffect(() => {
    if (active && cardRef.current) {
      const cardTop = cardRef.current.offsetTop;
      const cardHeight = cardRef.current.offsetHeight;
      
      const container = document.getElementsByClassName(styles.scroll);

      const containerHeight = container[0].clientHeight;
      const scrollTop = cardTop - (containerHeight / 2) + (cardHeight / 2) + 120;

      container[0].scrollTo({
        top: scrollTop,
        behavior: "smooth"
      });
    }
  }, []);


  return (
    <div
      className={cx(styles.assetCard, { [styles.active]: active })}
      onClick={() => onSelection(entity.id)}
      ref={cardRef}
    >
      <Thumbnail
        className={styles.thumbnail}
        media={entity.icon}
        defaultImage={defaultAsset}
      />
      <div className={styles.assetDetails}>
        <div className={styles.assetName}>
          <Text text={entity?.name || ""} lines={1} />
        </div>
      <div className={styles.assetType}>
        {active ? (
          <>
            currently viewing
            <span className={styles.loadingDots}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </>
        ) : (
          fileType
        )}
      </div>
      </div>
      <div className={styles.preview}>
        <i className={cx("paperflite-right-arrow", styles.previewIcon)} />
      </div>
    </div>
  );
};

const PlaceholderAssetCard = () => {
  return (
    <div className={cx(styles.placeholderAssetCard, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <div className={cx(styles.type, styles.block)}></div>
      </div>
    </div>
  );
};

const PlaceholderCard = () => {
  return (
    <div className={cx(styles.placeholderCard, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)} />
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)} />
        <div className={cx(styles.type, styles.block)} />
      </div>
    </div>
  );
};


export default EntityPanel;